import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Address } from '@checkout-v2/const';
import { DeliveryAddressService } from '@checkout-v2/feature';
import {
  DropdownComponent,
  DropdownMenuComponent,
  DropdownMenuItemComponent,
  DropdownTogglerComponent,
  SdBottomSheetService,
  SdModalService,
  SdvorIconComponent,
  SdvorRadioComponent,
} from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes, SdBottomSheetOptions } from '@shared/v2';
import { filter } from 'rxjs';
import { DeliveryAddressDialogComponent } from '../delivery-address-dialog';
import { DeliveryAddressOptionsComponent } from './delivery-address-options';

@Component({
  selector: 'sd-delivery-address',
  imports: [
    ReactiveFormsModule,
    SdvorRadioComponent,
    SdvorIconComponent,
    DropdownComponent,
    DropdownTogglerComponent,
    DropdownMenuComponent,
    DropdownMenuItemComponent,
  ],
  templateUrl: './delivery-address.component.html',
  styleUrls: ['./delivery-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryAddressComponent implements OnInit {
  protected readonly address = input.required<Address>();

  private readonly modalService = inject(SdModalService);
  private readonly deviceService = inject(DeviceService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly bottomSheetService = inject(SdBottomSheetService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isMobileDown = toSignal(this.deviceService.isMobileSmDown$, { initialValue: false });
  protected readonly activeAddress = toSignal(this.deliveryAddressService.activeAddress$, { initialValue: null });

  protected readonly addressControl = new FormControl<string>('', { nonNullable: true });
  protected readonly iconTypes = IconTypes;

  constructor() {
    effect(() => {
      const addressId = this.address().id;
      const activeId = this.activeAddress()?.id;
      const value = activeId && activeId === addressId ? (addressId ?? '') : '';

      if (this.addressControl.value !== value) {
        this.addressControl.setValue(value, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.addressControl.valueChanges.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.selectAddress();
    });
  }

  onOpenOptions(dropdown: DropdownComponent): void {
    if (this.isMobileDown()) {
      dropdown.close();
      this.openBottomSheet();
    } else {
      dropdown.toggle();
    }
  }

  openBottomSheet(): void {
    const height = 140;

    const options: SdBottomSheetOptions<{ onEdit: (event: Event) => void; onDelete: () => void }> = {
      breakpoints: [height],
      init: { type: 'px', value: height },
      minHeight: { type: 'px', value: height },
      hasCloseButton: false,
      ignoreOutsideClick: false,
      blockOverlay: true,
      background: 'white',
      onClose: () => {},
      style: { margin: '0px' },
      data: {
        onEdit: (event) => {
          this.bottomSheetService.close();
          this.editAddress(event);
        },
        onDelete: () => {
          this.bottomSheetService.close();
          this.deleteSavedAddress();
        },
      },
    };

    this.bottomSheetService.open(DeliveryAddressOptionsComponent, options);
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  deleteSavedAddress(): void {
    const addressId = this.address().id;
    if (addressId) this.deliveryAddressService.deleteSavedAddress(addressId);
  }

  selectAddress(): void {
    const addressId = this.address().id;
    if (addressId) this.deliveryAddressService.selectActiveAddressId(addressId);
  }

  editAddress(event: Event): void {
    const addressId = this.address().id;

    this.stopPropagation(event);

    if (addressId) {
      this.deliveryAddressService.editableAddressId$.next(addressId);

      this.modalService.open(DeliveryAddressDialogComponent, {
        modalType: { xxs: 'fullscreen', lg: 'standard' },
        maxWidth: { lg: '1240px' },
      });
    }
  }
}
